// src/app/core/chatPage/chatCopmonents/chatInForm/components/MessageItem.tsx
import {
  memo,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  AlertCircle, Check, Copy, RotateCcw, Search, Image as ImageIcon, Film,
  ChevronDown, Sparkles, Loader2, Brain, X, Play, FileText, Link2, Terminal, FileEdit, FilePlus, FolderOpen, Download, Paperclip, File as FileIcon,
} from "lucide-react";
import type { Message } from "../../shared";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { ModelAvatar } from "./providerUtils";
import { isContinuePause, workspaceView } from '@kabco/omega-ws';
import { ChatController } from "@/app/data/controllers/ChatController";
import { TextGenerationViewModel } from "../../shared/TextGenerationViewModel";
import { AuthApi } from "@/app/data/auth/AuthApi";

interface MessageItemProps {
  message: Message;
  modelDisplayName: string | null;
  modelDisplayNames: Record<string, string>;
  selectedModelIdByType: Record<string, string>;
  canRegenerate: boolean;
  onRegenerate: (messageId: string) => void;
  onConfirmResume?: (messageId: string, toolCallId: string, argumentsJson: string) => void;
  onRejectResume?: (messageId: string, toolCallId: string) => void;
  onContinueResume?: (messageId: string) => void;
}

// ---------- Утилиты ----------

function sanitizeMarkdown(text: string): string {
  if (!text) return text;
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/([.!?])([А-ЯA-ZЁ])/g, '$1 $2')
    .replace(/attached:.*$/gim, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function safeParse(json: string | null | undefined): any {
  if (!json) return null;
  try { return JSON.parse(json); } catch { return null; }
}

function safeHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try { return new URL(url).host; } catch { return url; }
}

function getExtensionFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const parts = pathname.split('.');
    if (parts.length > 1) {
      const ext = parts.pop()?.split('?')[0];
      if (ext && ext.length <= 5) return ext.toLowerCase();
    }
  } catch {}
  return '';
}

async function downloadFile(url: string, filename: string) {
  const tg = (window as any).Telegram?.WebApp;
  if (tg && typeof tg.downloadFile === 'function') {
    try {
      tg.downloadFile({ url, file_name: filename });
      return;
    } catch (e) {
      console.warn('Telegram downloadFile failed, fallback to fetch', e);
    }
  }

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Network response was not ok: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.warn('Download via fetch failed (probably CORS), falling back to open in new tab', error);
    window.open(url, '_blank');
  }
}

async function downloadShelfFile(path: string, filename: string) {
  try {
    // Если сервер вернул сразу абсолютную ссылку (S3 URL), качаем её напрямую
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return downloadFile(path, filename);
    }

    const vm = TextGenerationViewModel.getInstance();
    const conversationId = vm.activeConversationDbId;

    if (!conversationId) {
        alert('Диалог ещё не сохранён на сервере. Попробуйте позже.');
        return;
    }
    
    const chatController = ChatController.getInstance();
    
    // Пытаемся скачать через SDK (для путей полки)
    if (chatController.client && chatController.client.files) {
        try {
            const result = await chatController.client.files.download({
              conversationId,
              path
            });
            const blobUrl = window.URL.createObjectURL(result.blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename || result.fileName || 'download';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
            return;
        } catch (sdkError) {
            console.warn('SDK download failed, falling back to REST', sdkError);
        }
    }

    // Fallback: REST API
    const token = AuthApi.getAccessToken();
    let baseUrl = (import.meta.env.VITE_API_BASE_URL || 'https://gpt-bot.kabco.tech/api/v1').replace(/\/$/, '');
    // Проверяем, есть ли /api/v1 в конце URL
    if (!baseUrl.endsWith('/api/v1')) {
        baseUrl += '/api/v1';
    }
    
    const url = `${baseUrl}/client/conversations/${conversationId}/files/content?path=${encodeURIComponent(path)}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (e: any) {
    console.error('Shelf download failed', e);
    alert(`Не удалось скачать файл: ${e.message || 'Ошибка сервера'}`);
  }
}

// ---------- Модель консилиума ----------

type ConsiliumMember = {
  id: string;
  category: 'text' | 'image' | 'video' | 'search';
  label: string;
  provider?: string | null;
  active?: boolean;
};

function getTextModelInfo(message: Message, displayNames: Record<string, string>, fallbackLabel: string) {
  const id = message.modelId || '';
  const label = (id && displayNames[id]) || id || fallbackLabel;
  return { label, provider: message.provider || null };
}

function getImageModelInfo(
  message: Message,
  selectedIdByType: Record<string, string>,
  displayNames: Record<string, string>,
  fallbackLabel: string,
) {
  const imgTool = (message.tools ?? []).find((t: any) => t.toolName === 'prepare_image');
  const payload = imgTool?.lastProgress?.payload as any;
  const fromPayload = payload?.modelId || (safeParse(imgTool?.resultJson) as any)?.modelId;
  const id = fromPayload || selectedIdByType['image'];
  const label = (id && displayNames[id]) || id || fallbackLabel;
  return { label, provider: null };
}

function getVideoModelInfo(
  message: Message,
  selectedIdByType: Record<string, string>,
  displayNames: Record<string, string>,
  fallbackLabel: string,
) {
  const vidTool = (message.tools ?? []).find((t: any) => t.toolName === 'prepare_video');
  const payload = vidTool?.lastProgress?.payload as any;
  const fromPayload = payload?.modelId || (safeParse(vidTool?.resultJson) as any)?.modelId;
  const id = fromPayload || selectedIdByType['video'];
  const label = (id && displayNames[id]) || id || fallbackLabel;
  return { label, provider: null };
}

// ---------- Вложения пользователя ----------

function UserAttachments({ attachments }: { attachments: NonNullable<Message['attachments']> }) {
  const [isOpen, setIsOpen] = useState(false);

  const images = attachments.filter(a => a.kind === 'image');
  const filesCount = attachments.length;

  const summary = [];
  if (images.length) summary.push(`${images.length} фото`);
  if (filesCount > images.length) summary.push(`${filesCount - images.length} файл(ов)`);

  return (
    <div className="mb-2 border border-white/10 dark:border-white/5 bg-black/10 dark:bg-white/5 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(v => !v)}
        className="w-full flex items-center gap-2 p-2.5 text-xs text-foreground/80 hover:bg-white/5 transition-colors"
      >
        <Paperclip size={14} className="text-violet-400" />
        <span className="font-medium">Вложения ({summary.join(', ')})</span>
        <ChevronDown
          size={14}
          className={`ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="p-2 border-t border-white/10 dark:border-white/5 flex flex-wrap gap-2">
          {attachments.map((att, idx) => {
            if (att.kind === 'image') {
              return (
                <div key={idx} className="relative rounded-lg overflow-hidden border border-white/10 max-w-[120px] group">
                  <img src={att.url} alt={att.name} className="w-full h-auto object-cover" />
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[10px] text-white px-1 truncate">{att.name}</span>
                  </div>
                </div>
              );
            }
            return (
              <div key={idx} className="flex items-center gap-1.5 p-2 bg-black/20 dark:bg-white/10 rounded-lg text-xs text-foreground/80 w-full">
                {att.kind === 'video' ? <Film size={14} className="text-blue-400" /> : <FileIcon size={14} className="text-gray-400" />}
                <span className="truncate flex-1">{att.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- Шаги для пузыря ----------

type StepItem =
  | { kind: 'reasoning'; id: string; step: number; text: string; active: boolean }
  | { kind: 'bubble'; id: string; step: number; text: string }
  | { kind: 'tool'; id: string; step: number; tool: any };

function buildStepItems(agentSteps: any[], tools: any[], blocks: any[], isStreaming: boolean): StepItem[] {
  const items: StepItem[] = [];
  const stepNumbers = Array.from(new Set<number>([
    ...(agentSteps || []).map(s => (typeof s.step === 'number' ? s.step : 0)),
    ...(tools || []).map(t => (typeof t.step === 'number' ? t.step : 0)),
    ...(blocks || []).map(b => b.step),
  ])).sort((a, b) => a - b);

  for (const step of stepNumbers) {
    const agentStep = (agentSteps || []).find(s => s.step === step);
    if (agentStep) {
      const text = (agentStep.reasoningText || '').trim();
      if (text.length > 0) {
        items.push({
          kind: 'reasoning',
          id: `reasoning-${step}`,
          step,
          text,
          active: !!agentStep.reasoningActive && isStreaming,
        });
      }
    }

    const stepBlocks = (blocks || []).filter(b => b.step === step);
    for (const b of stepBlocks) {
      const t = (b.text || '').trim();
      if (!t) continue;
      items.push({
        kind: 'bubble',
        id: `bubble-${b.id}`,
        step,
        text: t,
      });
    }

    const stepTools = (tools || []).filter(t => (typeof t.step === 'number' ? t.step : 0) === step);
    for (const tool of stepTools) {
      items.push({ kind: 'tool', id: `tool-${tool.toolCallId}`, step, tool });
    }
  }

  return items;
}

// ---------- Подкомпоненты ----------

function ReasoningBlock({ text, isActive, label }: { text: string; isActive: boolean; label: string }) {
  const [isOpen, setIsOpen] = useState(isActive);

  useEffect(() => {
    setIsOpen(isActive);
  }, [isActive]);

  if (!text) return null;

  return (
    <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/30 dark:bg-white/5 overflow-hidden">
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-[11px] text-muted-foreground hover:bg-white/10 dark:hover:bg-white/5"
      >
        {isActive ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-3 pb-2 pt-0.5 text-xs italic text-muted-foreground/80 whitespace-pre-wrap">
          {text}
        </div>
      )}
    </div>
  );
}

function WorkspaceCard({ tool }: { tool: any }) {
  const frame = workspaceView(tool);
  
  if (!frame) {
    const args = safeParse(tool.argumentsJson) || {};
    const command = args.command || args.path || tool.toolName;
    return (
      <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm flex items-center gap-2">
        <Terminal size={14} className="text-slate-500 shrink-0" />
        <span className="font-medium text-foreground text-[13px] truncate">{command}</span>
        {tool.status !== 'COMPLETED' && tool.status !== 'FAILED' && <Loader2 size={12} className="text-muted-foreground animate-spin ml-auto shrink-0" />}
        {tool.status === 'COMPLETED' && <Check size={14} className="text-emerald-500 ml-auto shrink-0" />}
        {tool.status === 'FAILED' && <AlertCircle size={14} className="text-red-500 ml-auto shrink-0" />}
      </div>
    );
  }

  if (frame.kind === 'write' || frame.kind === 'edit') {
    const isEdit = frame.kind === 'edit';
    const Icon = isEdit ? FileEdit : FilePlus;
    const filename = frame.path.split('/').pop() || 'file';
    
    return (
      <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm flex items-center gap-2">
        <Icon size={14} className={isEdit ? "text-blue-500 shrink-0" : "text-emerald-500 shrink-0"} />
        <span className="font-medium text-foreground text-[13px]">{isEdit ? 'Изменен файл' : 'Создан файл'}</span>
        <span className="text-xs text-muted-foreground truncate flex-1">{frame.path}</span>
        
        <button 
          onClick={() => downloadShelfFile(frame.path, filename)}
          className="p-1.5 rounded-lg bg-violet-500/10 hover:bg-violet-500/20 text-violet-500 transition-colors"
          title="Скачать"
        >
          <Download size={14} />
        </button>
        
        <span className="text-[10px] text-muted-foreground bg-black/10 dark:bg-white/10 px-1.5 py-0.5 rounded">{frame.bytes} B</span>
      </div>
    );
  }

  if (frame.kind === 'command') {
    return (
      <div className="rounded-xl border border-white/10 dark:border-white/5 bg-[#1e1e1e] p-2.5 text-sm flex items-center gap-2">
        <Terminal size={14} className="text-slate-400 shrink-0" />
        <span className="font-medium text-[13px] text-slate-300 truncate flex-1">{frame.command}</span>
        {frame.exitCode !== null && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${frame.exitCode === 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
            exit {frame.exitCode}
          </span>
        )}
      </div>
    );
  }

  if (frame.kind === 'fault') {
    return (
      <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-2.5 text-sm flex items-center gap-2">
        <AlertCircle size={14} className="text-red-500 shrink-0" />
        <span className="font-medium text-[13px] text-red-500 truncate">{frame.message || frame.code}</span>
      </div>
    );
  }

  return null;
}

function ToolCard({
  tool,
  mediaPhase,
  labels,
}: {
  tool: any;
  mediaPhase?: string | null;
  labels: { imageGen: string; videoGen: string; awaiting: string; mediaPhases: Record<string, string> };
}) {
  if (tool.toolName === 'web_search') return <WebSearchCard tool={tool} />;
  if (tool.toolName === 'web_extract') return <WebExtractCard tool={tool} />;
  if (['write_file', 'edit_file', 'run_command'].includes(tool.toolName)) {
    return <WorkspaceCard tool={tool} />;
  }

  if (tool.toolName === 'read_file' || tool.toolName === 'list_dir') {
    const args = safeParse(tool.argumentsJson) || {};
    const path = args.path || args.dir || args.directory || (tool.toolName === 'list_dir' ? 'папка' : 'файл');
    const Icon = tool.toolName === 'list_dir' ? FolderOpen : FileText;
    
    return (
      <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm flex items-center gap-2">
        <Icon size={14} className="text-slate-500 shrink-0" />
        <span className="font-medium text-foreground text-[13px]">
          {tool.toolName === 'list_dir' ? 'Просмотр папки' : 'Чтение файла'}
        </span>
        <span className="text-xs text-muted-foreground truncate max-w-[120px]">{path}</span>
        {tool.status !== 'COMPLETED' && tool.status !== 'FAILED' && <Loader2 size={12} className="text-muted-foreground animate-spin ml-auto shrink-0" />}
        {tool.status === 'COMPLETED' && <Check size={14} className="text-emerald-500 ml-auto shrink-0" />}
        {tool.status === 'FAILED' && <AlertCircle size={14} className="text-red-500 ml-auto shrink-0" />}
      </div>
    );
  }

  const status = tool.status;
  const isDone = status === 'COMPLETED';
  const isFailed = status === 'FAILED';
  const isRunning = status === 'RUNNING' || status === 'REQUESTED';
  const isAwaiting = status === 'AWAITING_USER';
  const isMediaTool = tool.toolName === 'prepare_image' || tool.toolName === 'prepare_video';

  const label =
    tool.toolName === 'prepare_image' ? labels.imageGen :
    tool.toolName === 'prepare_video' ? labels.videoGen :
    tool.toolName;

  let phaseLabel = null;
  if (isMediaTool && isRunning) {
    if (mediaPhase) {
      phaseLabel = labels.mediaPhases[mediaPhase] || mediaPhase;
    } else if (tool.lastProgress?.phase) {
      phaseLabel = labels.mediaPhases[tool.lastProgress.phase] || tool.lastProgress.phase;
    }
  }

  return (
    <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm">
      <div className="flex items-center gap-2">
        {tool.toolName === 'prepare_image'
          ? <ImageIcon size={14} className="text-purple-500 shrink-0" />
          : tool.toolName === 'prepare_video'
            ? <Film size={14} className="text-pink-500 shrink-0" />
            : <span className="w-2 h-2 rounded-full bg-accent shrink-0" />}
        <span className="font-medium text-foreground text-[13px] truncate">{label}</span>
        {(isRunning || isAwaiting) && <Loader2 size={12} className="text-muted-foreground animate-spin ml-auto shrink-0" />}
        {isDone && <Check size={14} className="text-emerald-500 ml-auto shrink-0" />}
        {isFailed && <AlertCircle size={14} className="text-red-500 ml-auto shrink-0" />}
      </div>
      {phaseLabel && (
        <div className="text-xs text-muted-foreground mt-0.5">{phaseLabel}</div>
      )}
      {isAwaiting && <div className="text-xs text-amber-500 mt-0.5">{labels.awaiting}</div>}
      {isFailed && tool.errorMessage && <div className="text-xs text-red-500 mt-0.5 line-clamp-2">{tool.errorMessage}</div>}
    </div>
  );
}

function WebSearchCard({ tool }: { tool: any }) {
  const status = tool.status;
  const isDone = status === 'COMPLETED';
  const isFailed = status === 'FAILED';
  const last = tool.lastProgress;
  const phase: string | undefined = last?.phase;
  const payload: any = last?.payload || {};

  const header = (
    <div className="flex items-center gap-2 mb-2">
      <Search size={14} className="text-blue-500" />
      <span className="font-medium text-foreground text-[13px]">Поиск</span>
      {!isDone && !isFailed && <Loader2 size={12} className="text-muted-foreground animate-spin ml-auto shrink-0" />}
      {isDone && <Check size={14} className="text-emerald-500 ml-auto shrink-0" />}
      {isFailed && <AlertCircle size={14} className="text-red-500 ml-auto shrink-0" />}
    </div>
  );

  if (isDone && tool.resultJson) {
    const preview = safeParse(tool.resultJson);
    if (preview) {
      const sources: any[] = preview.sources || [];
      const media: any[] = preview.media || [];
      return (
        <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm">
          {header}
          {preview.query && (
            <div className="text-xs text-muted-foreground mb-2">
              Запрос: <span className="text-foreground">{preview.query}</span>
            </div>
          )}
          {media.length > 0 && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              {media.slice(0, 4).map((m: any, idx: number) => (
                <a key={idx} href={m.page_url} target="_blank" rel="noopener noreferrer"
                  className="rounded-lg overflow-hidden bg-white/5 border border-white/10 block">
                  {m.thumbnail_url
                    ? <img src={m.thumbnail_url} alt={m.title || ''} className="w-full h-20 object-cover" loading="lazy" />
                    : <div className="w-full h-20 flex items-center justify-center text-xs text-muted-foreground">{m.title || 'Изображение'}</div>}
                </a>
              ))}
            </div>
          )}
          {sources.length > 0 && (
            <div className="flex flex-col gap-1">
              {sources.slice(0, 5).map((s: any, idx: number) => (
                <a key={idx} href={s.url} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:text-blue-600 truncate">
                  {s.title || s.url}
                </a>
              ))}
            </div>
          )}
        </div>
      );
    }
  }

  return (
    <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm">
      {header}
      {phase === 'planning' && <div className="text-xs text-muted-foreground mb-2">Планирую запросы…</div>}
      
      {phase === 'queries' && Array.isArray(payload.queries) && payload.queries.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {payload.queries.map((q: any, idx: number) => (
            <span key={idx} className="text-[10px] bg-blue-500/10 text-blue-500 px-1.5 py-0.5 rounded-full truncate max-w-[150px]">
              {q.query}
            </span>
          ))}
        </div>
      )}
      
      {phase === 'hit' && (
        <>
          <div className="text-xs text-muted-foreground mb-1">
            Получаю ссылки ({payload.done ?? 0} / {payload.of ?? 0})
          </div>
          {Array.isArray(payload.urls) && payload.urls.length > 0 && (
            <div className="flex flex-col gap-1 mt-1">
              {payload.urls.slice(0, 3).map((u: any, idx: number) => (
                <a key={idx} href={u.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:text-blue-600 truncate">
                  {u.title || u.url}
                </a>
              ))}
            </div>
          )}
        </>
      )}

      {phase === 'ranked' && <div className="text-xs text-muted-foreground mb-2">Ранжирую источники…</div>}
      {phase === 'reading' && <div className="text-xs text-muted-foreground mb-2">Читаю страницы…</div>}

      {isFailed && tool.errorMessage && <div className="text-xs text-red-500 mt-1">{tool.errorMessage}</div>}
    </div>
  );
}

function WebExtractCard({ tool }: { tool: any }) {
  const status = tool.status;
  const isDone = status === 'COMPLETED';
  const isFailed = status === 'FAILED';
  const isRunning = status === 'RUNNING' || status === 'REQUESTED';
  const isAwaiting = status === 'AWAITING_USER';

  const args = safeParse(tool.argumentsJson) || {};
  const last = tool.lastProgress;
  const phase: string | undefined = last?.phase;
  const payload: any = last?.payload || {};
  const preview: any = isDone ? safeParse(tool.resultJson) : null;

  type PageEntry = { url: string; title?: string; error?: string; truncated?: boolean };

  const pages: PageEntry[] = (() => {
    const out: PageEntry[] = [];
    if (Array.isArray(preview?.pages)) {
      for (const p of preview.pages) {
        if (!p?.url) continue;
        out.push({
          url: p.url,
          title: typeof p.title === 'string' ? p.title : undefined,
          error: typeof p.error === 'string' ? p.error : undefined,
          truncated: Boolean(p.truncated),
        });
      }
    }
    if (out.length === 0) {
      const fromArgs: string[] = Array.isArray(args.urls)
        ? args.urls
        : (typeof args.url === 'string' ? [args.url] : []);
      const fromPayload: string[] = Array.isArray(payload.urls)
        ? payload.urls
        : (typeof payload.url === 'string' ? [payload.url] : []);
      const seen = new Set<string>();
      for (const u of [...fromArgs, ...fromPayload]) {
        if (typeof u !== 'string' || !u || seen.has(u)) continue;
        seen.add(u);
        out.push({ url: u });
      }
    }
    return out;
  })();

  const hasAnyError = pages.some(p => p.error);
  const hasOnlyErrors = pages.length > 0 && pages.every(p => p.error);
  const hasContent = pages.some(p => !p.error);
  const showSuccess = isDone && hasContent;
  const showError = isFailed || (isDone && hasOnlyErrors);
  const showWarn = isDone && !showSuccess && !showError && hasAnyError;

  const phaseLabelMap: Record<string, string> = {
    queued: 'В очереди',
    fetching: 'Загружаю страницу',
    parsing: 'Разбираю содержимое',
    reading: 'Читаю страницу',
    storing: 'Сохраняю',
    running: 'Читаю страницу',
  };

  const headerText = pages.length === 1 && pages[0].title
    ? pages[0].title
    : 'Чтение страницы';

  return (
    <div className="rounded-xl border border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5 p-2.5 text-sm">
      <div className="flex items-center gap-2">
        <FileText size={14} className="text-indigo-500 shrink-0" />
        <span className="font-medium text-foreground text-[13px] truncate flex-1">
          {headerText}
        </span>
        {(isRunning || isAwaiting) && <Loader2 size={12} className="text-muted-foreground animate-spin shrink-0" />}
        {showSuccess && <Check size={14} className="text-emerald-500 shrink-0" />}
        {showError && <AlertCircle size={14} className="text-red-500 shrink-0" />}
        {showWarn && <AlertCircle size={14} className="text-amber-500 shrink-0" />}
      </div>

      {isRunning && phase && (
        <div className="text-xs text-muted-foreground mt-0.5">
          {phaseLabelMap[phase] || phase}
        </div>
      )}

      {isAwaiting && (
        <div className="text-xs text-amber-500 mt-0.5">Жду подтверждения</div>
      )}

      {pages.length > 0 && (
        <ul className="mt-1.5 flex flex-col gap-1">
          {pages.slice(0, 4).map((p, idx) => {
            const host = safeHost(p.url) || p.url;
            const failed = Boolean(p.error);
            return (
              <li key={`${p.url}-${idx}`} className="flex items-start gap-1.5 text-xs">
                {failed
                  ? <AlertCircle size={11} className="text-red-500 shrink-0 mt-0.5" />
                  : <Link2 size={11} className="text-blue-500 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`truncate block ${failed ? 'text-muted-foreground' : 'text-blue-500 hover:text-blue-600'}`}
                  >
                    {p.title || host}
                  </a>
                  {failed && (
                    <div className="text-[10px] text-red-500 truncate mt-0.5">{p.error}</div>
                  )}
                  {!failed && p.truncated && (
                    <div className="text-[10px] text-amber-500 mt-0.5">обрезано</div>
                  )}
                </div>
              </li>
            );
          })}
          {pages.length > 4 && (
            <li className="text-[10px] text-muted-foreground pl-4">и ещё {pages.length - 4}…</li>
          )}
        </ul>
      )}

      {isFailed && tool.errorMessage && (
        <div className="text-xs text-red-500 mt-1">{tool.errorMessage}</div>
      )}
    </div>
  );
}

// ---------- Confirm/Reject карточка паузы ----------

function ConfirmCard({
  message,
  isContinue,
  labels,
  onConfirm,
  onReject,
  onContinue,
}: {
  message: Message;
  isContinue: boolean;
  labels: {
    awaitingTitle: string;
    continueTitle: string;
    confirm: string;
    reject: string;
    imageTitle: string;
    videoTitle: string;
  };
  onConfirm: () => void;
  onReject: () => void;
  onContinue: () => void;
}) {
  const pause = message.lastPaused;
  if (!pause) return null;

  if (isContinue) {
    return (
      <div className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-3">
        <div className="text-xs text-amber-700 dark:text-amber-400 mb-2">
          {pause.prompt || labels.awaitingTitle}
        </div>
        <button
          onClick={onContinue}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/90 hover:bg-amber-500 text-white text-xs font-medium"
        >
          <Play size={12} /> {labels.continueTitle}
        </button>
      </div>
    );
  }

  const toolLabel =
    pause.toolName === 'prepare_image' ? labels.imageTitle :
    pause.toolName === 'prepare_video' ? labels.videoTitle :
    pause.toolName;

  return (
    <div className="rounded-2xl border border-violet-400/30 bg-violet-500/5 p-3">
      <div className="flex items-start gap-2 mb-2">
        <Sparkles size={14} className="text-violet-500 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-foreground">{toolLabel}</div>
          {pause.prompt && (
            <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">
              {pause.prompt}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onConfirm}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 text-white text-xs font-medium"
        >
          <Check size={12} /> {labels.confirm}
        </button>
        <button
          onClick={onReject}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/50 dark:bg-white/10 hover:bg-white/70 dark:hover:bg-white/15 text-foreground text-xs font-medium border border-white/20 dark:border-white/10"
        >
          <X size={12} /> {labels.reject}
        </button>
      </div>
    </div>
  );
}

// ---------- Чипы консилиума ----------

function ConsiliumRow({ members, compact = false }: { members: ConsiliumMember[]; compact?: boolean }) {
  if (members.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {members.map((m) => (
        <div
          key={m.id}
          className={`inline-flex items-center gap-1 rounded-full border ${
            m.active
              ? 'border-violet-400/40 bg-violet-500/10'
              : 'border-white/10 dark:border-white/5 bg-white/40 dark:bg-white/5'
          } px-2 py-0.5 text-[10px] font-medium text-foreground/80`}
        >
          <ModelAvatar provider={m.provider} size={12} />
          {!compact && <span className="truncate max-w-[120px]">{m.label}</span>}
        </div>
      ))}
    </div>
  );
}

// ---------- План ----------

function PlanCard({ plan }: { plan: Message['plan'] }) {
  if (!plan || plan.length === 0) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <Check size={14} className="text-emerald-500" />;
      case 'in_progress': return <Loader2 size={14} className="text-blue-500 animate-spin" />;
      case 'cancelled': return <X size={14} className="text-red-500" />;
      default: return <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 dark:border-white/20" />;
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 dark:border-white/10 bg-white/20 dark:bg-white/[0.03] p-3 mb-2">
      <div className="text-xs font-semibold text-foreground/80 mb-2 flex items-center gap-1.5">
        <FileText size={12} className="text-violet-500" />
        План действий
      </div>
      <div className="flex flex-col gap-1.5">
        {plan.map((item, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div className="mt-0.5 shrink-0">{getStatusIcon(item.status)}</div>
            <span className={`text-xs ${item.status === 'completed' ? 'text-muted-foreground line-through' : 'text-foreground/90'}`}>
              {item.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Пузырь шагов (reasoning + tool) ----------

function AgentBubble({
  items,
  isStreaming,
  isPaused,
  machinePhase,
  mediaPhase,
  consilium,
  labels,
}: {
  items: StepItem[];
  isStreaming: boolean;
  isPaused?: boolean;
  machinePhase?: string | null;
  mediaPhase?: string | null;
  consilium: ConsiliumMember[];
  labels: {
    awaiting: string;
    thinking: string;
    steps: string;
    tools: string;
    starting: string;
    noSteps: string;
    reasoning: string;
    toolLabels: { imageGen: string; videoGen: string; awaiting: string; mediaPhases: Record<string, string> };
  };
}) {
  const [userOpen, setUserOpen] = useState(false);
  const open = isStreaming || userOpen;

  const toolsCount = items.filter(i => i.kind === 'tool').length;
  const reasoningCount = items.filter(i => i.kind === 'reasoning').length;

  let headerText = `${labels.steps} · ${reasoningCount + toolsCount}`;
  if (isPaused) headerText = labels.awaiting;
  else if (machinePhase) headerText = `Машина: ${machinePhase}`;
  else if (isStreaming) headerText = labels.thinking;

  return (
    <div className="rounded-2xl border border-white/10 dark:border-white/10 bg-white/20 dark:bg-white/[0.03] overflow-hidden">
      <button
        type="button"
        onClick={() => setUserOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 hover:bg-white/10 dark:hover:bg-white/5"
      >
        {isStreaming && !isPaused && !machinePhase
          ? <Loader2 size={14} className="text-violet-500 animate-spin shrink-0" />
          : machinePhase
            ? <Terminal size={14} className="text-slate-400 animate-pulse shrink-0" />
            : <Sparkles size={14} className="text-violet-500 shrink-0" />}

        <span className="text-xs font-medium text-foreground/80 text-left truncate">
          {headerText}
          {toolsCount > 0 && !isStreaming && !machinePhase && (
            <span className="text-muted-foreground/70"> · {toolsCount} {labels.tools}</span>
          )}
        </span>

        <div className="flex-1 flex justify-end">
          <ConsiliumRow members={consilium} compact />
        </div>

        <ChevronDown
          size={16}
          className={`text-muted-foreground transition-transform duration-200 shrink-0 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="flex flex-col gap-2 px-3 pb-3 pt-1">
          {machinePhase && (
            <div className="text-xs text-slate-400 font-mono flex items-center gap-1.5 px-1">
              <Loader2 size={10} className="animate-spin" /> {machinePhase}...
            </div>
          )}

          {items.length > 0 ? (
            items.map((item) => {
              if (item.kind === 'reasoning') {
                return (
                  <ReasoningBlock
                    key={item.id}
                    text={item.text}
                    isActive={item.active}
                    label={labels.reasoning}
                  />
                );
              }
              if (item.kind === 'bubble') {
                return (
                  <div key={item.id} className="text-sm leading-relaxed text-foreground break-words px-1 py-0.5">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{sanitizeMarkdown(item.text)}</ReactMarkdown>
                  </div>
                );
              }
              return (
                <ToolCard
                  key={item.id}
                  tool={item.tool}
                  mediaPhase={mediaPhase}
                  labels={labels.toolLabels}
                />
              );
            })
          ) : (
            !machinePhase && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
                <Brain size={12} />
                <span>{isStreaming ? labels.starting : labels.noSteps}</span>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Текст ответа ----------

const FinalText = memo(function FinalText({ text, isStreaming }: { text: string; isStreaming: boolean }) {
  if (!text) return null;

  if (isStreaming) {
    return (
      <div
        className="text-sm leading-relaxed text-foreground break-words px-1"
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      >
        {text}
        <span className="inline-block w-2 h-2 rounded-full bg-accent ml-2 align-middle animate-pulse" />
      </div>
    );
  }

  return (
    <div className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words px-1
      prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0 prose-blockquote:my-0.5
      prose-pre:my-0.5 prose-h1:my-0.5 prose-h2:my-0.5 prose-h3:my-0.5 prose-h4:my-0.5">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{text}</ReactMarkdown>
    </div>
  );
});

// ---------- Основной компонент ----------

export function MessageItem({
  message,
  modelDisplayName,
  modelDisplayNames,
  selectedModelIdByType,
  canRegenerate,
  onRegenerate,
  onConfirmResume,
  onRejectResume,
  onContinueResume,
}: MessageItemProps) {
  const { t } = useTranslation();

  const labels = {
    aiAssistant: t('aiAssistant'),
    generationError: t('generationError'),
    stopped: t('stopped'),
    tokensUsedLabel: t('tokensUsedLabel'),
    tokens: t('tokens'),
    copy: t('copy'),
    regenerate: t('regenerate'),
    agentAwaiting: t('agentAwaiting'),
    agentThinking: t('agentThinking'),
    stepsLabel: t('stepsLabel'),
    toolsLabel: t('toolsLabel'),
    agentStarting: t('agentStarting'),
    noSteps: t('noSteps'),
    imageModelLabel: t('imageModelLabel'),
    videoModelLabel: t('videoModelLabel'),
    searchToolLabel: t('searchToolLabel'),
    extractToolLabel: t('extractToolLabel'),
    reasoning: t('reasoning'),
    confirm: t('confirm'),
    reject: t('reject'),
    continue: t('continue'),
  };

  const isUser = message.sender === "user";
  const isStopped = Boolean(message.isStopped);
  const isError = Boolean(message.error) && !isStopped;
  const [isCopied, setIsCopied] = useState(false);

  const bubbleBg = isUser
    ? 'bg-violet-500/10 dark:bg-violet-500/20 backdrop-blur-lg border border-violet-400/20 dark:border-violet-400/30 shadow-lg'
    : 'bg-white/70 dark:bg-white/5 backdrop-blur-lg border border-white/20 dark:border-white/10 shadow-md';

  const canCopy = Boolean(message.text?.trim());

  const handleCopy = useCallback(async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(message.text);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 1600);
    } catch (error) {
      console.error('[MessageItem] Copy failed', error);
    }
  }, [canCopy, message.text]);

  const tools = message.tools ?? [];
  const mediaParts = message.mediaParts ?? [];
  const agentSteps = message.agentSteps ?? [];
  const blocks = message.blocks ?? [];
  const isStreaming = Boolean(message.isStreaming);

  const textInfo = getTextModelInfo(message, modelDisplayNames, modelDisplayName || labels.aiAssistant);

  const consilium: ConsiliumMember[] = (() => {
    const members: ConsiliumMember[] = [];
    members.push({ id: 'text', category: 'text', label: textInfo.label, provider: textInfo.provider, active: false });

    const hasImage = tools.some((tool: any) => tool.toolName === 'prepare_image');
    const hasVideo = tools.some((tool: any) => tool.toolName === 'prepare_video');
    const hasSearch = tools.some((tool: any) => tool.toolName === 'web_search');
    const hasExtract = tools.some((tool: any) => tool.toolName === 'web_extract');

    if (hasImage) {
      const info = getImageModelInfo(message, selectedModelIdByType, modelDisplayNames, labels.imageModelLabel);
      members.push({ id: 'image', category: 'image', label: info.label, provider: info.provider });
    }
    if (hasVideo) {
      const info = getVideoModelInfo(message, selectedModelIdByType, modelDisplayNames, labels.videoModelLabel);
      members.push({ id: 'video', category: 'video', label: info.label, provider: info.provider });
    }
    if (hasSearch) members.push({ id: 'search', category: 'search', label: labels.searchToolLabel });
    if (hasExtract) members.push({ id: 'extract', category: 'search', label: labels.extractToolLabel });

    return members;
  })();

  let finalText = '';
  let intermediateBlocks: any[] = [];

  if (isStreaming) {
    intermediateBlocks = blocks;
  } else {
    const textBlocks = blocks.filter((b: any) => (b.text || '').trim().length > 0);
    if (textBlocks.length > 0) {
      const lastBlock = textBlocks[textBlocks.length - 1];
      finalText = lastBlock.text;
      intermediateBlocks = blocks.filter((b: any) => b.id !== lastBlock.id);
    } else {
      finalText = message.text || '';
      intermediateBlocks = blocks;
    }
  }

  const sanitizedFinal = sanitizeMarkdown(finalText.trim());
  const stepItems = buildStepItems(agentSteps, tools, intermediateBlocks, isStreaming);

  const paused = message.lastPaused;
  const isPausedState = message.state === 'PAUSED' && Boolean(paused);
  const isContinue = paused ? isContinuePause(paused as any) : false;
  const pausedTool = paused
    ? (tools.find((tl: any) => tl.toolCallId === paused.toolCallId) ?? null)
    : null;
  const isApprovalPause = isPausedState && !isContinue && pausedTool?.status === 'AWAITING_USER';

  const hasAgentContext =
    Boolean(isStreaming) ||
    stepItems.length > 0 ||
    Boolean(message.machinePhase);

  const shouldShowMedia = mediaParts.length > 0 && !isStreaming;

  const headerLabel = isError
    ? (textInfo.label ? `${labels.generationError} · ${textInfo.label}` : labels.generationError)
    : textInfo.label;

  if (import.meta.env.DEV && !isUser) {
    // eslint-disable-next-line no-console
    console.log('[MessageItem] snapshot', {
      id: message.id,
      isStreaming: message.isStreaming,
      isWaiting: message.isWaiting,
      state: message.state,
      isPaused: message.isPaused,
      pauseKind: paused?.kind,
      toolStatus: pausedTool?.status,
      agentSteps: agentSteps.length,
      stepItems: stepItems.length,
      blocks: blocks.length,
      tools: tools.length,
      mediaParts: mediaParts.length,
      mediaPhase: message.mediaPhase,
      machinePhase: message.machinePhase,
      textLen: (message.text || '').length,
    });
  }

  // ---------- Агентский ответ ----------
  if (!isUser && (hasAgentContext || shouldShowMedia || message.plan || message.files)) {
    const showTyping = isStreaming && !sanitizedFinal && !isPausedState && !message.machinePhase && stepItems.length === 0;

    return (
      <div className="flex justify-start group">
        <div className="relative max-w-[85%] w-full flex flex-col gap-2">
          
          {/* План действий */}
          {message.plan && <PlanCard plan={message.plan} />}

          {hasAgentContext && (
            <AgentBubble
              items={stepItems}
              isStreaming={isStreaming}
              isPaused={isPausedState}
              machinePhase={message.machinePhase}
              mediaPhase={message.mediaPhase ?? null}
              consilium={consilium}
              labels={{
                awaiting: labels.agentAwaiting,
                thinking: labels.agentThinking,
                steps: labels.stepsLabel,
                tools: labels.toolsLabel,
                starting: labels.agentStarting,
                noSteps: labels.noSteps,
                reasoning: labels.reasoning,
                toolLabels: {
                  imageGen: labels.imageModelLabel,
                  videoGen: labels.videoModelLabel,
                  awaiting: labels.agentAwaiting,
                  mediaPhases: {
                    queued: t('phaseQueued'),
                    running: t('phaseRunning'),
                    fetching: t('phaseFetching'),
                    storing: t('phaseStoring'),
                  },
                },
              }}
            />
          )}

          {isPausedState && paused && (isContinue || isApprovalPause) && (
            <ConfirmCard
              message={message}
              isContinue={isContinue}
              labels={{
                awaitingTitle: labels.agentAwaiting,
                continueTitle: labels.continue,
                confirm: labels.confirm,
                reject: labels.reject,
                imageTitle: labels.imageModelLabel,
                videoTitle: labels.videoModelLabel,
              }}
              onConfirm={() => onConfirmResume?.(
                message.id,
                paused.toolCallId,
                paused.argumentsJson,
              )}
              onReject={() => onRejectResume?.(
                message.id,
                paused.toolCallId,
              )}
              onContinue={() => onContinueResume?.(message.id)}
            />
          )}

          {showTyping && (
            <div className="flex items-center gap-2 px-1 py-1.5">
              <ModelAvatar provider={textInfo.provider} size={16} />
              <span className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground/80">{textInfo.label}</span> печатает
              </span>
              <span className="flex gap-1">
                {[0, 1, 2].map((i) => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </span>
            </div>
          )}

          {!showTyping && (sanitizedFinal || isStreaming) && (
            <FinalText text={sanitizedFinal} isStreaming={isStreaming && !isPausedState} />
          )}

          {isError && (
            <div className="p-2.5 rounded-xl bg-red-500/5 border border-red-500/10 text-sm">
              <div className="flex items-center gap-2 text-red-600 font-medium mb-1">
                <AlertCircle size={14} />
                {labels.generationError}
              </div>
              <p className="text-foreground/80">{message.error}</p>
            </div>
          )}

          {shouldShowMedia && mediaParts.map((media, idx) => (
            <div key={`media-${media.mediaId ?? idx}-${media.url}`}
              className="relative group rounded-2xl overflow-hidden shadow-md bg-white/5 border border-white/10">
              
              {media.kind === 'video'
                ? <video src={media.url} controls className="w-full h-auto rounded-2xl" />
                : <img src={media.url} alt="Media" className="w-full h-auto object-cover rounded-2xl" loading="lazy" />}
                
              <button
                onClick={() => {
                  const ext = getExtensionFromUrl(media.url) || (media.kind === 'video' ? 'mp4' : 'jpg');
                  downloadFile(media.url, `media-${media.mediaId || idx}.${ext}`);
                }}
                className="absolute top-2 right-2 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full backdrop-blur-sm transition-colors"
                title="Скачать"
              >
                <Download size={16} />
              </button>
            </div>
          ))}

          {/* Файлы результата */}
          {message.files && message.files.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-1">
              {message.files.map((file, idx) => (
                <button 
                  key={idx}
                  onClick={() => downloadShelfFile(file.path, file.name)}
                  className="flex items-center gap-2 p-2 bg-white/40 dark:bg-white/5 rounded-lg border border-white/10 hover:bg-white/60 dark:hover:bg-white/10 transition-colors text-left"
                >
                  <div className="p-1.5 bg-violet-500/10 rounded-md shrink-0">
                    <Download size={14} className="text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{file.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{file.path}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between px-1">
            <span className="text-[10px] text-muted-foreground">
              {message.tokensUsed != null && !isStreaming && (
                <>
                  {labels.tokensUsedLabel} <span className="font-medium text-foreground">{message.tokensUsed}</span> {labels.tokens} ·{' '}
                </>
              )}
              {message.timestamp.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
            </span>
            <div className="flex gap-0.5">
              {canCopy && !isStreaming && (
                <button onClick={handleCopy} className="p-1 rounded-md hover:bg-muted text-muted-foreground" aria-label={labels.copy}>
                  {isCopied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              )}
              {canRegenerate && (
                <button onClick={() => onRegenerate(message.id)} className="p-1 rounded-md hover:bg-muted text-muted-foreground" aria-label={labels.regenerate}>
                  <RotateCcw size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Простой режим ----------
  const renderSimple = () => {
    if (isUser) {
      return (
        <div className="flex flex-col gap-2">
          {message.attachments && message.attachments.length > 0 && (
            <UserAttachments attachments={message.attachments} />
          )}
          {message.text && message.text !== '[Прикреплен файл]' && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{sanitizeMarkdown(message.text)}</p>
          )}
        </div>
      );
    }

    if (message.isWaiting) {
      return (
        <div className="flex items-center gap-2 py-1.5">
          <ModelAvatar provider={textInfo.provider} size={16} />
          <span className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground/80">{textInfo.label}</span> печатает
          </span>
          <span className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <span key={i} className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </span>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="p-2.5 rounded-xl bg-red-500/5 border border-red-500/10 text-sm">
          <div className="flex items-center gap-2 text-red-600 font-medium mb-1">
            <AlertCircle size={14} />
            {labels.generationError}
          </div>
          <p className="text-foreground/80">{message.error}</p>
        </div>
      );
    }

    if (isStreaming) {
      return (
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {sanitizedFinal}
          <span className="inline-block w-2 h-2 rounded-full bg-accent ml-2 align-middle animate-pulse" />
        </p>
      );
    }

    return (
      <div className="text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert break-words
        prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0 prose-blockquote:my-0.5
        prose-pre:my-0.5 prose-h1:my-0.5 prose-h2:my-0.5 prose-h3:my-0.5 prose-h4:my-0.5">
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{sanitizedFinal}</ReactMarkdown>
      </div>
    );
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} group`}>
      <div className={`relative max-w-[85%] ${bubbleBg} rounded-2xl px-3.5 py-2.5 ${isUser ? 'rounded-br-md' : 'rounded-bl-md'} break-words`}>
        {!isUser && (
          <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground">
            <ModelAvatar provider={textInfo.provider} size={14} />
            <span>{headerLabel}</span>
            {isStopped && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 text-[10px] font-medium">
                {labels.stopped}
              </span>
            )}
          </div>
        )}

        {renderSimple()}

        {!isUser && !message.isWaiting && !isStreaming && !isError && message.tokensUsed != null && (
          <div className="mt-1.5 text-[10px] text-muted-foreground flex items-center gap-1 self-end">
            <span>{labels.tokensUsedLabel}</span>
            <span className="font-medium text-foreground">{message.tokensUsed}</span>
            <span>{labels.tokens}</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-1.5 self-end">
          <span className="text-[10px] text-muted-foreground">
            {message.timestamp.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}
          </span>
          <div className="flex gap-0.5">
            {!isUser && canCopy && (
              <button onClick={handleCopy} className="p-1 rounded-md hover:bg-muted text-muted-foreground" aria-label={labels.copy}>
                {isCopied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            )}
            {canRegenerate && (
              <button onClick={() => onRegenerate(message.id)} className="p-1 rounded-md hover:bg-muted text-muted-foreground" aria-label={labels.regenerate}>
                <RotateCcw size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}